import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Alert, Button } from "antd";
import {
  ArrowLeftOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNewTrialState } from "../../legacy";
import {
  usePlotSelection,
  type AssignedSeedPlot,
} from "../../context/PlotSelectionContext";
import { useTrialsRouter } from "../../../../shared/custom-hooks/useTrialsRouter";
import useSeedCatalog, {
  type SeedCropDetails,
} from "../../hooks/useSeedCatalog";
import useSeedTranslations from "../../hooks/useSeedTranslations";
import {
  validateSeedRate,
  type SeedAssignmentErrors,
} from "../../utils/seedValidation";
import {
  buildSeedSavePayload,
  convertSeedRate,
  fetchSeedAutoAssignments,
  fetchSeedTrial,
  hydrateSeedAutoAssignmentRows,
  hydrateSeedRows,
  nonEmptyString,
  patchSeedAssignments,
  patchSeedStandard,
  resetAllSeedAssignments,
  resetSeedAssignments,
  seedApiError,
  seedAutoAssignmentQueryKey,
  seedTrialQueryKey,
  type SeedAssignmentRow,
  type SeedPlot,
  type SeedProduct,
  type SeedTrial,
} from "../../services/seedAssignments";
import { triggerSeedAgOperation } from "../../services/seedAgOperation";
import { runAsync } from "../../utils/runAsync";
import seedRateDisplayUnit from "../../utils/seedRateDisplayUnit";
import {
  getSeedProductColor,
  getSeedProductFamilyColor,
} from "../../utils/seedProductColors";
import AssignmentGroups from "../AssignmentGroups";
import StdSeedRate from "../StdSeedRate";
import AutoProdAssignment from "../AutoProdAssignment";
import { ErrorAutoScreen } from "../ErrorAutoScreen";
import PlotSummary from "../PlotSummary";
import ProductCard from "../ProductCard";
import ResetBtn from "../ResetBtn";
import ManualProductPlots, {
  type ManualProductPlot,
} from "../ManualProductPlots";
import WarningConfirmationPopover from "../../../../shared/components/WarningConfirmationPopover";
import "./style.less";

interface DensitySettings {
  density_range?: {
    min?: number;
    max?: number;
  } | null;
  density_options?: number[] | null;
}

interface ProtocolProduct extends DensitySettings {
  id: string;
  variety: string;
  measure_unit?: string;
}

interface SeedProtocol {
  crop_details?: SeedCropDetails;
  product_details?: {
    seeds?: DensitySettings & {
      global_density_range?: boolean;
      measure_unit?: string;
      products?: ProtocolProduct[];
    };
  };
}

export type BeforeLeaveSeeds = (
  createAgOperation?: boolean
) => Promise<boolean>;
export type BeforeBackSeeds = () => Promise<boolean>;
export type ResetSeeds = () => Promise<boolean>;

interface SeedsTabProps {
  showAutoAssignment: boolean;
  hasMockAssignments: boolean;
  onShowAutoAssignment: (show: boolean) => void;
  onCompleteAutoAssignment: () => void;
  active?: boolean;
  onContinue?: () => void;
  onManualPreviewChange?: (plotIds: string[]) => void;
  onValidationChange?: (valid: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  onRegisterBeforeLeave?: (handler: BeforeLeaveSeeds | null) => void;
  onRegisterBeforeBack?: (handler: BeforeBackSeeds | null) => void;
  onRegisterReset?: (handler: ResetSeeds | null) => void;
  onResetAvailabilityChange?: (available: boolean) => void;
}

interface StandardDraft {
  productId: string;
  productName: string;
  rate: number | null;
}

interface SeedResetSnapshot {
  trial: SeedTrial;
  rows: SeedAssignmentRow[];
}

const EMPTY_STANDARD: StandardDraft = {
  productId: "",
  productName: "",
  rate: null,
};

const seedAssignmentDraftPlots = (
  rows: SeedAssignmentRow[],
  products: SeedProduct[]
): AssignedSeedPlot[] =>
  rows.reduce<AssignedSeedPlot[]>((plots, row) => {
    if (row.productId !== "") {
      const product = products.find((item) => item.id === row.productId);
      plots.push({
        cellId: row.id,
        ownerId: row.productId,
        ownerLabel: row.variety,
        seedRate: row.seedRate,
        color:
          product === undefined
            ? nonEmptyString(row.color) ?? getSeedProductFamilyColor(0)
            : getSeedProductColor(
                product,
                products,
                row.seedRate,
                rows
                  .filter(
                    (item) =>
                      item.productId === row.productId &&
                      item.plotGroupId === row.plotGroupId
                  )
                  .map((item) => item.seedRate)
              ),
      });
    }

    return plots;
  }, []);

const standardFromTrial = (trial: SeedTrial): StandardDraft => ({
  productId: trial.standard_field_properties?.standard_seed_product_id ?? "",
  productName: trial.standard_field_properties?.standard_product ?? "",
  rate: trial.standard_field_properties?.standard_seed_rate ?? null,
});

const SeedsTabContent = ({
  showAutoAssignment,
  onShowAutoAssignment,
  active = true,
  onContinue,
  onManualPreviewChange,
  onValidationChange,
  onDirtyChange,
  onSavingChange,
  onRegisterBeforeLeave,
  onRegisterBeforeBack,
  onRegisterReset,
  onResetAvailabilityChange,
}: SeedsTabProps): JSX.Element => {
  const text = useSeedTranslations();
  const queryClient = useQueryClient();
  const { orgId, workspaceId } = useTrialsRouter();

  const { trialInfo, trialProtocolData, newTrialState, setRatesDosage } =
    useNewTrialState();

  const {
    session,
    focusedId,
    setAssignedSeedPlots,
    setAutoAssignmentDraftPlots,
    startSession,
    endSession,
    setSelectedIds,
    setSeedSelectionRates,
    toggleFocus,
  } = usePlotSelection();

  const trialId = trialInfo?.id ?? "";
  const protocol = trialProtocolData as unknown as SeedProtocol | null;
  const seeds = protocol?.product_details?.seeds;
  const unit = seeds?.measure_unit ?? "seeds/ha";
  const displayUnit = seedRateDisplayUnit(unit);

  const scope = useMemo(
    () => ({
      orgId: orgId ?? "",
      workspaceId: workspaceId ?? "",
    }),
    [orgId, workspaceId]
  );

  const queryKey = useMemo(
    () => seedTrialQueryKey(trialId, scope),
    [trialId, scope]
  );

  const autoQueryKey = useMemo(
    () => seedAutoAssignmentQueryKey(trialId, scope),
    [trialId, scope]
  );

  const trialQuery = useQuery({
    queryKey,
    enabled:
      trialId.trim() !== "" && protocol !== null && protocol !== undefined,
    queryFn: async ({ signal }) => await fetchSeedTrial(trialId, scope, signal),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const catalog = useSeedCatalog(
    trialInfo?.protocol_id,
    protocol?.crop_details
  );

  const productResult = useMemo(() => {
    try {
      const products = (seeds?.products ?? []).map(
        (product, index): SeedProduct => {
          const global = seeds?.global_density_range ?? false;
          const settings = global ? seeds : product;
          const sourceUnit = global
            ? seeds?.measure_unit ?? unit
            : product.measure_unit ?? seeds?.measure_unit ?? unit;

          const min = settings?.density_range?.min;
          const max = settings?.density_range?.max;

          return {
            id: product.id,
            name: product.variety,
            color: getSeedProductFamilyColor(index),
            range:
              typeof min === "number" && typeof max === "number"
                ? {
                    min: convertSeedRate(min, sourceUnit, unit),
                    max: convertSeedRate(max, sourceUnit, unit),
                  }
                : undefined,
            options: (settings?.density_options ?? []).map((rate) =>
              convertSeedRate(rate, sourceUnit, unit)
            ),
          };
        }
      );

      return {
        products,
        error: null as string | null,
      };
    } catch (error) {
      return {
        products: [] as SeedProduct[],
        error: error instanceof Error ? error.message : "Invalid seed units.",
      };
    }
  }, [seeds, unit]);

  const products = productResult.products;

  const hydration = useMemo(() => {
    if (trialQuery.data === undefined) {
      return {
        rows: [] as SeedAssignmentRow[],
        error: null as string | null,
      };
    }

    try {
      return {
        rows: hydrateSeedRows(
          trialQuery.data,
          (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
          products,
          unit
        ),
        error: null as string | null,
      };
    } catch (error) {
      return {
        rows: [] as SeedAssignmentRow[],
        error:
          error instanceof Error
            ? error.message
            : "Could not restore seed assignments.",
      };
    }
  }, [trialQuery.data, newTrialState.trial_plots, products, unit]);

  const [persisted, setPersisted] = useState<SeedAssignmentRow[]>([]);
  const [tableDraft, setTableDraft] = useState<SeedAssignmentRow[]>([]);
  const [autoAssignmentDraft, setAutoAssignmentDraft] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [autoAssignmentSuccess, setAutoAssignmentSuccess] = useState(false);
  const [overwriteConfirmationOpen, setOverwriteConfirmationOpen] =
    useState(false);
  const [autoAssignmentFailed, setAutoAssignmentFailed] = useState(false);
  const [manualProductId, setManualProductId] = useState<string | null>(null);
  const [manualRates, setManualRates] = useState<Record<string, number | null>>(
    {}
  );
  const [standard, setStandard] = useState<StandardDraft>(EMPTY_STANDARD);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadRequired, setReloadRequired] = useState(false);

  const operationLock = useRef(false);
  const seedAgOperationCreatedFor = useRef<string | null>(null);
  const acceptedVersion = useRef("");
  const standardInitializedFor = useRef("");
  const mounted = useRef(true);
  const previewTrialId = useRef(trialId);

  useEffect(() => {
    if (previewTrialId.current === trialId) return;

    previewTrialId.current = trialId;
    setAutoAssignmentDraftPlots([]);
    setAutoAssignmentSuccess(false);
  }, [trialId, setAutoAssignmentDraftPlots]);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      runAsync(
        queryClient.cancelQueries({
          queryKey: autoQueryKey,
          exact: true,
        })
      );
    };
  }, [queryClient, autoQueryKey]);

  const tableDirty =
    autoAssignmentDraft ||
    JSON.stringify(tableDraft) !== JSON.stringify(persisted);

  const serverStandard = trialQuery.data
    ? standardFromTrial(trialQuery.data)
    : EMPTY_STANDARD;

  const standardDirty =
    JSON.stringify(standard) !== JSON.stringify(serverStandard);

  const busy = saving || refreshing || autoAssigning;
  const draftOpen = manualProductId !== null || tableDirty;

  useEffect(() => {
    if (
      trialQuery.data === undefined ||
      hydration.error !== null ||
      operationLock.current ||
      draftOpen
    ) {
      return;
    }

    const version = JSON.stringify([trialId, hydration.rows]);

    if (acceptedVersion.current === version) {
      return;
    }

    acceptedVersion.current = version;
    setPersisted(hydration.rows);
    setTableDraft(hydration.rows);
  }, [trialQuery.data, hydration, draftOpen, trialId, busy]);

  useEffect(() => {
    if (trialQuery.data === undefined || operationLock.current) {
      return;
    }

    const initialized = standardInitializedFor.current === trialId;
    const nextStandard = standardFromTrial(trialQuery.data);
    standardInitializedFor.current = trialId;
    setStandard((current) =>
      initialized && JSON.stringify(current) !== JSON.stringify(nextStandard)
        ? current
        : nextStandard
    );
  }, [trialQuery.data, trialId, busy]);

  useEffect(() => {
    setAssignedSeedPlots(
      persisted
        .filter((row) => row.productId !== "")
        .map((row) => ({
          cellId: row.id,
          ownerId: row.productId,
          ownerLabel: row.variety,
          seedRate: row.seedRate,
          color: (() => {
            const product = products.find((item) => item.id === row.productId);
            return product === undefined
              ? nonEmptyString(row.color) ?? getSeedProductFamilyColor(0)
              : getSeedProductColor(
                  product,
                  products,
                  row.seedRate,
                  persisted
                    .filter(
                      (item) =>
                        item.productId === row.productId &&
                        item.plotGroupId === row.plotGroupId
                    )
                    .map((item) => item.seedRate)
                );
          })(),
        }))
    );
  }, [persisted, products, setAssignedSeedPlots]);

  useEffect(() => {
    setAutoAssignmentDraftPlots(
      autoAssignmentDraft ? seedAssignmentDraftPlots(tableDraft, products) : []
    );
  }, [autoAssignmentDraft, tableDraft, products, setAutoAssignmentDraftPlots]);

  const selectedIds = useMemo(() => {
    if (session?.tab === "seeds" && session.ownerId === manualProductId) {
      return session.selectedIds;
    }

    return [] as string[];
  }, [session, manualProductId]);

  useEffect(() => {
    onManualPreviewChange?.(selectedIds);
  }, [selectedIds, onManualPreviewChange]);

  const manualProduct = products.find(
    (product) => product.id === manualProductId
  );

  const getRateError = (
    rate: number | null,
    product?: SeedProduct
  ): string | undefined => {
    const error = validateSeedRate(rate, product?.range);

    if (error?.kind === "required") {
      return "Enter a valid seed rate.";
    }

    if (error?.kind === "range") {
      return `Rate must be between ${error.min} and ${error.max}.`;
    }

    if (
      rate !== null &&
      product !== undefined &&
      product.options.length > 0 &&
      !product.options.includes(rate)
    ) {
      return `Allowed rates: ${product.options.join(", ")}.`;
    }

    return undefined;
  };

  const standardValid =
    nonEmptyString(standard.productId) !== undefined &&
    nonEmptyString(standard.productName) !== undefined &&
    getRateError(standard.rate) === undefined;

  const hasFieldStandardVariety =
    nonEmptyString(standard.productId) !== undefined &&
    nonEmptyString(standard.productName) !== undefined;

  const manualPlots: ManualProductPlot[] = selectedIds.flatMap((id) => {
    const row = persisted.find((item) => item.id === id);

    if (row === undefined || manualProduct === undefined) {
      return [];
    }

    const rate = Object.prototype.hasOwnProperty.call(manualRates, id)
      ? manualRates[id]
      : manualProduct.options[0] ?? manualProduct.range?.min ?? null;

    return [
      {
        id,
        group: row.group,
        plot: row.plot,
        seedRate: rate,
      },
    ];
  });

  useEffect(() => {
    setSeedSelectionRates(
      Object.fromEntries(manualPlots.map((plot) => [plot.id, plot.seedRate]))
    );
  }, [manualPlots, setSeedSelectionRates]);

  const manualErrors: Record<string, string | undefined> = Object.fromEntries(
    manualPlots.map((plot) => [
      plot.id,
      getRateError(plot.seedRate, manualProduct),
    ])
  );

  const canSaveManual =
    manualProduct !== undefined &&
    manualPlots.length > 0 &&
    manualPlots.every((plot) => manualErrors[plot.id] === undefined) &&
    !busy &&
    !reloadRequired;

  const tableErrors: Record<string, SeedAssignmentErrors> = {};

  tableDraft.forEach((row) => {
    if (row.productId === "") {
      return;
    }

    const product = products.find((item) => item.id === row.productId);

    tableErrors[row.id] = {
      product:
        product !== undefined || row.isStandard
          ? undefined
          : "This product is not available in the protocol.",
      rate: getRateError(row.seedRate, product),
    };
  });

  const canSaveTable =
    tableDirty &&
    tableDraft.some(
      (row) =>
        row.productId !== "" &&
        !row.legacy &&
        (autoAssignmentDraft || !row.isStandard)
    ) &&
    tableDraft.every(
      (row) =>
        row.productId === "" ||
        (tableErrors[row.id]?.product === undefined &&
          tableErrors[row.id]?.rate === undefined)
    ) &&
    !busy &&
    !reloadRequired;

  const reviewingGeneratedAssignments =
    showAutoAssignment && autoAssignmentSuccess;
  const completionRows = reviewingGeneratedAssignments ? tableDraft : persisted;
  const seedsComplete =
    trialQuery.data !== undefined &&
    !trialQuery.isError &&
    productResult.error === null &&
    completionRows.length > 0 &&
    completionRows.length === hydration.rows.length &&
    hydration.error === null &&
    completionRows.every((row) => {
      const product = products.find((item) => item.id === row.productId);

      return (
        row.productId !== "" &&
        (product !== undefined || row.isStandard) &&
        getRateError(row.seedRate, product) === undefined
      );
    }) &&
    standardValid &&
    (!draftOpen || reviewingGeneratedAssignments) &&
    (!showAutoAssignment || reviewingGeneratedAssignments) &&
    !busy &&
    !reloadRequired;

  useEffect(() => {
    onValidationChange?.(seedsComplete);
  }, [seedsComplete, onValidationChange]);

  useEffect(() => {
    onDirtyChange?.(draftOpen || standardDirty || showAutoAssignment);
  }, [draftOpen, standardDirty, showAutoAssignment, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(busy);
  }, [busy, onSavingChange]);

  useEffect(() => {
    return () => {
      onSavingChange?.(false);
    };
  }, [onSavingChange]);

  useEffect(() => {
    if (active || operationLock.current || manualProductId === null) {
      return;
    }

    endSession({
      tab: "seeds",
      ownerId: manualProductId,
    });
    setManualProductId(null);
    setManualRates({});
  }, [active, manualProductId, endSession]);

  const latestSession = useRef(session);
  latestSession.current = session;

  useEffect(() => {
    return () => {
      const current = latestSession.current;

      if (current?.tab === "seeds") {
        endSession({
          tab: "seeds",
          ownerId: current.ownerId,
        });
      }
    };
  }, [endSession]);

  const standardOptions = useMemo(() => {
    const options = (catalog.data ?? []).map((product) => ({
      value: product.id,
      label: product.commercial_name,
    }));

    if (
      standard.productId !== "" &&
      !options.some((option) => option.value === standard.productId)
    ) {
      options.push({
        value: standard.productId,
        label: nonEmptyString(standard.productName) ?? standard.productId,
      });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [catalog.data, standard.productId, standard.productName]);

  const reconcile = (trial: SeedTrial, rows: SeedAssignmentRow[]): void => {
    acceptedVersion.current = JSON.stringify([trialId, rows]);
    setPersisted(rows);
    setTableDraft(rows);
    setAutoAssignmentDraft(false);
    setAutoAssignmentDraftPlots([]);
    setAutoAssignmentSuccess(false);
    queryClient.setQueryData(queryKey, trial);
  };

  const refreshServerState = async (): Promise<SeedTrial> => {
    await queryClient.cancelQueries({
      queryKey,
      exact: true,
    });

    const fresh = await fetchSeedTrial(trialId, scope);
    queryClient.setQueryData(queryKey, fresh);

    await queryClient.invalidateQueries(
      {
        queryKey: ["trial", trialId],
      },
      {
        throwOnError: true,
      }
    );

    runAsync(
      queryClient.invalidateQueries({
        queryKey: ["trialInsights"],
      })
    );

    return fresh;
  };

  const handleResetSeedAssignments = async (
    productId: string
  ): Promise<void> => {
    const trial = trialQuery.data;

    if (
      trial === undefined ||
      operationLock.current ||
      busy ||
      reloadRequired ||
      manualProductId !== productId
    ) {
      return;
    }

    const ownedRows = persisted.filter((row) => row.productId === productId);

    if (ownedRows.some((row) => row.isStandard || row.legacy)) {
      setErrorMessage(
        "Standard and legacy assignments cannot be reset in this editor."
      );
      return;
    }

    if (ownedRows.length === 0) {
      setSelectedIds([]);
      setManualRates({});
      setErrorMessage(null);
      return;
    }

    operationLock.current = true;
    setSaving(true);
    setErrorMessage(null);

    let resetSucceeded = false;
    let confirmedReset: SeedResetSnapshot | null = null;

    try {
      await queryClient.cancelQueries({
        queryKey,
        exact: true,
      });

      const response = await resetSeedAssignments(trial, productId, scope);

      resetSucceeded = true;

      if (!Array.isArray(response?.seed_product_assignments)) {
        throw new Error(
          "The reset API returned an invalid seed-assignment response."
        );
      }

      const updatedTrial: SeedTrial = {
        ...trial,
        plot_property_assignments: {
          ...trial.plot_property_assignments,
          ...response,
        },
      };

      const updatedRows = hydrateSeedRows(
        updatedTrial,
        (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
        products,
        unit
      );

      if (updatedRows.some((row) => row.productId === productId)) {
        throw new Error(
          "The reset response still contains assignments for this product."
        );
      }

      confirmedReset = {
        trial: updatedTrial,
        rows: updatedRows,
      };

      reconcile(updatedTrial, updatedRows);

      endSession({
        tab: "seeds",
        ownerId: productId,
      });
      setManualProductId(null);
      setManualRates({});
      onManualPreviewChange?.([]);

      const freshTrial = await refreshServerState();
      const freshRows = hydrateSeedRows(
        freshTrial,
        (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
        products,
        unit
      );

      if (freshRows.some((row) => row.productId === productId)) {
        throw new Error(
          "The refreshed trial still contains assignments for this product."
        );
      }

      reconcile(freshTrial, freshRows);
    } catch (error) {
      if (resetSucceeded) {
        if (confirmedReset !== null) {
          reconcile(confirmedReset.trial, confirmedReset.rows);
        }

        setReloadRequired(true);
        setErrorMessage(
          "The reset request succeeded, but the latest seed assignments " +
            "could not be confirmed. Reload before continuing."
        );
      } else {
        const failure = seedApiError(error);
        setReloadRequired(failure.reloadRequired);
        setErrorMessage(failure.message);
      }
    } finally {
      operationLock.current = false;
      setSaving(false);
    }
  };

  const topResetProductIds = Array.from(
    persisted.reduce((productsWithRows, row) => {
      if (row.productId === "") return productsWithRows;

      const current = productsWithRows.get(row.productId) ?? [];
      current.push(row);
      productsWithRows.set(row.productId, current);
      return productsWithRows;
    }, new Map<string, SeedAssignmentRow[]>())
  )
    .filter(([, rows]) => rows.every((row) => !row.isStandard && !row.legacy))
    .map(([productId]) => productId);
  const topResetAvailable =
    topResetProductIds.length > 0 &&
    !draftOpen &&
    !standardDirty &&
    !showAutoAssignment &&
    !busy &&
    !reloadRequired;

  const handleResetAllSeedAssignments = async (): Promise<boolean> => {
    const trial = trialQuery.data;

    if (
      trial === undefined ||
      !topResetAvailable ||
      operationLock.current ||
      busy ||
      reloadRequired
    ) {
      return false;
    }

    operationLock.current = true;
    setSaving(true);
    setErrorMessage(null);

    let resetSucceeded = false;
    let confirmedReset: SeedResetSnapshot | null = null;

    const confirmReset = (updatedTrial: SeedTrial): SeedAssignmentRow[] => {
      const rows = hydrateSeedRows(
        updatedTrial,
        (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
        products,
        unit
      );

      if (rows.some((row) => topResetProductIds.includes(row.productId))) {
        throw new Error(
          "The reset response still contains applicable seed assignments."
        );
      }

      return rows;
    };

    try {
      await queryClient.cancelQueries({ queryKey, exact: true });

      const response = await resetAllSeedAssignments(
        trial,
        topResetProductIds,
        scope
      );

      if (response === null) {
        return false;
      }

      resetSucceeded = true;

      if (!Array.isArray(response.seed_product_assignments)) {
        throw new Error(
          "The reset API returned an invalid seed-assignment response."
        );
      }

      const updatedTrial: SeedTrial = {
        ...trial,
        plot_property_assignments: {
          ...trial.plot_property_assignments,
          ...response,
        },
      };
      const updatedRows = confirmReset(updatedTrial);
      confirmedReset = { trial: updatedTrial, rows: updatedRows };
      reconcile(updatedTrial, updatedRows);

      const freshTrial = await refreshServerState();
      const freshRows = confirmReset(freshTrial);
      reconcile(freshTrial, freshRows);
      onValidationChange?.(false);

      return true;
    } catch (error) {
      if (resetSucceeded) {
        if (confirmedReset !== null) {
          reconcile(confirmedReset.trial, confirmedReset.rows);
        }

        setReloadRequired(true);
        setErrorMessage(
          "The reset request succeeded, but the latest seed assignments " +
            "could not be confirmed. Reload before continuing."
        );
      } else {
        const failure = seedApiError(error);
        setReloadRequired(failure.reloadRequired);
        setErrorMessage(failure.message);
      }

      return false;
    } finally {
      operationLock.current = false;
      setSaving(false);
    }
  };

  useEffect(() => {
    onRegisterReset?.(handleResetAllSeedAssignments);

    return () => {
      onRegisterReset?.(null);
    };
  }, [handleResetAllSeedAssignments, onRegisterReset]);

  useEffect(() => {
    onResetAvailabilityChange?.(topResetAvailable);
  }, [onResetAvailabilityChange, topResetAvailable]);

  const saveRows = async (
    nextRows: SeedAssignmentRow[],
    explicitlySaved: string[],
    createAgOperation = false,
    replaceAllAssignments = false
  ): Promise<boolean> => {
    const trial = trialQuery.data;

    if (trial === undefined || operationLock.current || reloadRequired) {
      return false;
    }

    const previousSession = session;
    operationLock.current = true;
    setSaving(true);
    setErrorMessage(null);

    if (previousSession?.tab === "seeds") {
      endSession({
        tab: "seeds",
        ownerId: previousSession.ownerId,
      });
    }

    let saved = false;

    try {
      const payload = buildSeedSavePayload(
        trial,
        persisted,
        nextRows,
        products,
        unit,
        explicitlySaved,
        { replaceAllAssignments }
      );

      await queryClient.cancelQueries({
        queryKey,
        exact: true,
      });

      const response = await patchSeedAssignments(trialId, payload, scope);
      saved = true;

      const updated: SeedTrial = {
        ...trial,
        plot_property_assignments: response,
      };

      const restored = hydrateSeedRows(
        updated,
        (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
        products,
        unit
      );

      reconcile(updated, restored);
      setManualProductId(null);
      setManualRates({});
      setAutoAssignmentDraft(false);
      setAutoAssignmentSuccess(false);
      onShowAutoAssignment(false);

      let agOperationError: string | null = null;

      // Only Auto Product Assignment Save enables this trigger.
      // Manual product Save retains its existing behavior.
      //
      // Trigger before refreshing queries, without an is_exportable gate.
      if (createAgOperation) {
        try {
          await triggerSeedAgOperation(updated, ["seed"], scope);
          seedAgOperationCreatedFor.current = trialId;
        } catch (error) {
          const reason =
            error instanceof Error
              ? error.message
              : "An unexpected error occurred.";

          agOperationError =
            "Product assignments were saved, but the agricultural " +
            `operation creation request could not be confirmed. ${reason}`;

          // Keep successfully saved assignments. Do not automatically
          // retry a POST whose server-side outcome may be uncertain.
          setErrorMessage(agOperationError);
        }
      }

      // Refresh independently, including when the operation trigger fails.
      try {
        const fresh = await refreshServerState();
        const freshRows = hydrateSeedRows(
          fresh,
          (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
          products,
          unit
        );

        reconcile(fresh, freshRows);
      } catch {
        setReloadRequired(true);

        const refreshError =
          "Assignments were saved, but trial status could not " +
          "be refreshed. Reload before continuing.";

        setErrorMessage(
          agOperationError === null
            ? refreshError
            : `${agOperationError} ${refreshError}`
        );

        return false;
      }

      return agOperationError === null;
    } catch (error) {
      const failure = seedApiError(error);

      setErrorMessage(
        saved
          ? "Assignments were saved, but their response could " +
              "not be reconciled. Reload before continuing."
          : failure.message
      );
      setReloadRequired(saved || failure.reloadRequired);

      if (!saved && previousSession?.tab === "seeds") {
        startSession({
          tab: previousSession.tab,
          ownerId: previousSession.ownerId,
          applicationId: previousSession.applicationId,
          targetMode: previousSession.targetMode,
          targetIds: previousSession.targetIds,
          selectedIds: previousSession.selectedIds,
          disabledIds: previousSession.disabledIds,
          color: previousSession.color,
        });
      }

      return false;
    } finally {
      operationLock.current = false;
      setSaving(false);
    }
  };

  const openManualProduct = (product: SeedProduct): void => {
    if (busy || reloadRequired || tableDirty || manualProductId !== null) {
      return;
    }

    const owned = persisted.filter(
      (row) => row.productId === product.id && !row.isStandard
    );

    if (owned.some((row) => row.legacy)) {
      setErrorMessage(
        "This product has legacy assignments without product_id. " +
          "Backend migration is required before editing."
      );
      return;
    }

    setErrorMessage(null);
    setManualProductId(product.id);
    setManualRates(
      Object.fromEntries(owned.map((row) => [row.id, row.seedRate]))
    );

    startSession({
      tab: "seeds",
      ownerId: product.id,
      targetMode: "plot",
      targetIds: persisted.map((row) => row.id),
      selectedIds: owned.map((row) => row.id),
      disabledIds: persisted
        .filter((row) => row.isStandard || row.legacy)
        .map((row) => row.id),
      color: product.color,
    });
  };

  const cancelManual = (): void => {
    if (busy) {
      return;
    }

    if (manualProductId !== null) {
      endSession({
        tab: "seeds",
        ownerId: manualProductId,
      });
    }

    setManualProductId(null);
    setManualRates({});

    if (!reloadRequired) {
      setErrorMessage(null);
    }
  };

  const saveManual = async (): Promise<boolean> => {
    if (manualProduct === undefined || !canSaveManual) {
      return false;
    }

    const selected = new Map(
      manualPlots.map((plot) => [plot.id, plot.seedRate])
    );

    const nextRows = persisted.map((row): SeedAssignmentRow => {
      if (selected.has(row.id)) {
        return {
          ...row,
          productId: manualProduct.id,
          variety: manualProduct.name,
          seedRate: selected.get(row.id) ?? null,
          isStandard: false,
          legacy: false,
          color:
            row.productId === manualProduct.id
              ? nonEmptyString(row.color) ?? manualProduct.color
              : manualProduct.color,
        };
      }

      if (row.productId === manualProduct.id) {
        return {
          ...row,
          productId: "",
          variety: "",
          seedRate: null,
          color: "",
          isStandard: false,
          legacy: false,
        };
      }

      return row;
    });

    return await saveRows(nextRows, [manualProduct.id]);
  };

  /**
   * Fetch suggestions only on an explicit user action.
   * Generated assignments remain local until Save succeeds.
   */
  const openAutoAssignment = async (): Promise<void> => {
    const trial = trialQuery.data;

    if (
      trial === undefined ||
      trialId.trim() === "" ||
      operationLock.current ||
      busy ||
      reloadRequired ||
      !hasFieldStandardVariety ||
      manualProductId !== null ||
      tableDirty
    ) {
      return;
    }

    operationLock.current = true;
    setAutoAssigning(true);
    setErrorMessage(null);
    setAutoAssignmentSuccess(false);

    try {
      const response = await queryClient.fetchQuery({
        queryKey: autoQueryKey,
        queryFn: async ({ signal }) =>
          await fetchSeedAutoAssignments(trialId, scope, signal),
        staleTime: 0,
        gcTime: 0,
        retry: false,
      });

      if (!mounted.current) {
        return;
      }

      const suggestedRows = hydrateSeedAutoAssignmentRows(
        trial,
        response,
        (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
        products,
        unit
      );

      if (
        suggestedRows.length !== persisted.length ||
        suggestedRows.some(
          (row) => !persisted.some((saved) => saved.id === row.id)
        )
      ) {
        throw new Error(
          "The generated assignments do not match the current plot design. " +
            "Reload the trial before trying again."
        );
      }

      setTableDraft(suggestedRows);
      setAutoAssignmentDraft(true);
      setAutoAssignmentSuccess(true);
      setAutoAssignmentFailed(false);
      onShowAutoAssignment(true);
    } catch (error) {
      if (!mounted.current) {
        return;
      }

      console.error("Failed to auto-assign seed products:", error);
      setAutoAssignmentFailed(true);
    } finally {
      operationLock.current = false;

      if (mounted.current) {
        setAutoAssigning(false);
      }
    }
  };

  const saveTable = async (): Promise<void> => {
    if (!canSaveTable) {
      return;
    }

    const beforeById = new Map(persisted.map((row) => [row.id, row]));

    const productsToSave = tableDraft
      .filter((row) => {
        if (
          row.productId === "" ||
          row.legacy ||
          (row.isStandard && !autoAssignmentDraft)
        ) {
          return false;
        }

        return (
          autoAssignmentDraft ||
          JSON.stringify(row) !== JSON.stringify(beforeById.get(row.id))
        );
      })
      .map((row) => row.productId);

    // Enable the seed-only ag_operation POST for this Save.
    if (
      await saveRows(
        tableDraft,
        [...new Set(productsToSave)],
        true,
        autoAssignmentDraft
      )
    ) {
      onContinue?.();
    }
  };

  const reload = async (): Promise<void> => {
    if (operationLock.current) {
      return;
    }

    operationLock.current = true;
    setRefreshing(true);

    try {
      await queryClient.cancelQueries({
        queryKey,
        exact: true,
      });

      const fresh = await fetchSeedTrial(trialId, scope);
      const rows = hydrateSeedRows(
        fresh,
        (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
        products,
        unit
      );

      endSession();
      setManualProductId(null);
      setManualRates({});
      onShowAutoAssignment(false);
      reconcile(fresh, rows);

      const restoredStandard = standardFromTrial(fresh);
      standardInitializedFor.current = trialId;
      setStandard(restoredStandard);

      setRatesDosage((previous) => ({
        ...previous,
        standard_seed_product_id: restoredStandard.productId,
        standard_product: restoredStandard.productName,
        standard_seed_rate: restoredStandard.rate ?? 0,
        unit_of_rate: unit,
      }));

      await queryClient.invalidateQueries(
        {
          queryKey: ["trial", trialId],
        },
        {
          throwOnError: true,
        }
      );

      setReloadRequired(false);
      setErrorMessage(null);
    } catch (error) {
      setReloadRequired(true);
      setErrorMessage(seedApiError(error).message);
    } finally {
      operationLock.current = false;
      setRefreshing(false);
    }
  };

  /**
   * Save a dirty field standard before allowing forward navigation.
   */
  const saveBeforeLeave = async (requireComplete = true): Promise<boolean> => {
    if (operationLock.current || busy || reloadRequired) {
      return false;
    }

    if (requireComplete && !seedsComplete) {
      setErrorMessage(
        "Complete and save the seed assignments, and enter a valid " +
          "field standard product and rate before continuing."
      );
      return false;
    }

    if (!standardDirty) {
      return true;
    }

    const rate = standard.rate;

    if (rate === null || !standardValid) {
      return false;
    }

    operationLock.current = true;
    setSaving(true);
    setErrorMessage(null);

    let saved = false;

    try {
      await queryClient.cancelQueries({
        queryKey,
        exact: true,
      });

      const currentTrial = await fetchSeedTrial(trialId, scope);

      await patchSeedStandard(
        currentTrial,
        {
          standard_seed_product_id: standard.productId,
          standard_product: standard.productName,
          standard_seed_rate: rate,
        },
        scope
      );

      saved = true;

      const fresh = await refreshServerState();
      const rows = hydrateSeedRows(
        fresh,
        (newTrialState.trial_plots ?? []) as unknown as SeedPlot[],
        products,
        unit
      );

      const restoredStandard = standardFromTrial(fresh);

      if (
        restoredStandard.productId !== standard.productId ||
        restoredStandard.productName !== standard.productName ||
        restoredStandard.rate !== rate
      ) {
        throw new Error(
          "The saved field standard could not be confirmed. " +
            "Reload before continuing."
        );
      }

      if (
        requireComplete &&
        (rows.length === 0 ||
          rows.some((row) => {
            const product = products.find((item) => item.id === row.productId);

            return (
              row.productId === "" ||
              (product === undefined && !row.isStandard) ||
              getRateError(row.seedRate, product) !== undefined
            );
          }))
      ) {
        throw new Error(
          "Seed assignments changed while saving. " +
            "Reload and verify them before continuing."
        );
      }

      reconcile(fresh, rows);
      standardInitializedFor.current = trialId;
      setStandard(restoredStandard);

      setRatesDosage((previous) => ({
        ...previous,
        standard_seed_product_id: restoredStandard.productId,
        standard_product: restoredStandard.productName,
        standard_seed_rate: restoredStandard.rate ?? 0,
        unit_of_rate: unit,
      }));

      return true;
    } catch (error) {
      const failure = seedApiError(error);

      setErrorMessage(
        saved
          ? "The field standard was saved, but the latest trial state " +
              "could not be confirmed. Reload before continuing."
          : failure.message
      );
      setReloadRequired(saved || failure.reloadRequired);

      return false;
    } finally {
      operationLock.current = false;
      setSaving(false);
    }
  };

  const saveBeforeBack = async (): Promise<boolean> => {
    if (operationLock.current || busy || reloadRequired) {
      return false;
    }

    if (manualProductId !== null) {
      if (!canSaveManual) {
        setErrorMessage("Correct the seed assignments before leaving Seeds.");
        return false;
      }

      if (!(await saveManual())) return false;
    }

    if (tableDirty) {
      if (!canSaveTable) {
        setErrorMessage("Correct the seed assignments before leaving Seeds.");
        return false;
      }

      const beforeById = new Map(persisted.map((row) => [row.id, row]));
      const productsToSave = tableDraft
        .filter(
          (row) =>
            row.productId !== "" &&
            !row.legacy &&
            (autoAssignmentDraft || !row.isStandard) &&
            (autoAssignmentDraft ||
              JSON.stringify(row) !== JSON.stringify(beforeById.get(row.id)))
        )
        .map((row) => row.productId);

      if (
        !(await saveRows(
          tableDraft,
          [...new Set(productsToSave)],
          false,
          autoAssignmentDraft
        ))
      ) {
        return false;
      }
    }

    return await saveBeforeLeave(false);
  };

  const prepareSeedsForNavigation = async (
    createAgOperation = false
  ): Promise<boolean> => {
    if (!(await saveBeforeLeave())) {
      return false;
    }

    if (!createAgOperation || seedAgOperationCreatedFor.current === trialId) {
      return true;
    }

    if (operationLock.current) {
      return false;
    }

    operationLock.current = true;
    setSaving(true);
    setErrorMessage(null);

    try {
      const fresh = await fetchSeedTrial(trialId, scope);

      await triggerSeedAgOperation(fresh, ["seed"], scope);
      seedAgOperationCreatedFor.current = trialId;

      return true;
    } catch (error) {
      const reason =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred.";

      setErrorMessage(
        "Your saved seed assignments have been retained, but the " +
          "agricultural operation creation request could not be " +
          `confirmed. ${reason}`
      );

      return false;
    } finally {
      operationLock.current = false;
      setSaving(false);
    }
  };

  useLayoutEffect(() => {
    onRegisterBeforeBack?.(saveBeforeBack);

    return () => {
      onRegisterBeforeBack?.(null);
    };
  });

  useLayoutEffect(() => {
    onRegisterBeforeLeave?.(prepareSeedsForNavigation);

    return () => {
      onRegisterBeforeLeave?.(null);
    };
  });

  const updateTableAssignment = (
    id: string,
    update: Partial<Pick<SeedAssignmentRow, "productId" | "seedRate">>
  ): void => {
    if (busy || reloadRequired) {
      return;
    }

    const current = tableDraft.find((row) => row.id === id);

    if (current === undefined) {
      return;
    }

    if ((current.isStandard || current.legacy) && !autoAssignmentDraft) {
      setErrorMessage("Standard and legacy assignments cannot be edited here.");
      return;
    }

    setTableDraft((previous) =>
      previous.map((row) => {
        if (row.id !== id) {
          return row;
        }

        const nextProductId = update.productId ?? row.productId;
        const product = products.find((item) => item.id === nextProductId);
        const productChanged = nextProductId !== row.productId;

        return {
          ...row,
          ...update,
          isStandard: productChanged ? false : row.isStandard,
          legacy: productChanged ? false : row.legacy,
          variety: productChanged ? product?.name ?? "" : row.variety,
          color: productChanged ? product?.color ?? "" : row.color,
        };
      })
    );
  };

  const closeAssignmentEditor = (): void => {
    if (busy || operationLock.current) {
      return;
    }

    if (autoAssignmentDraft && tableDirty) {
      const productsToSave = tableDraft
        .filter(
          (row) =>
            row.productId !== "" &&
            !row.legacy &&
            (autoAssignmentDraft || !row.isStandard)
        )
        .map((row) => row.productId);
      void saveRows(
        tableDraft,
        [...new Set(productsToSave)],
        false,
        autoAssignmentDraft
      );
      return;
    }

    setTableDraft(persisted);
    setAutoAssignmentDraft(false);
    setAutoAssignmentDraftPlots([]);
    setAutoAssignmentSuccess(false);
    onShowAutoAssignment(false);

    if (!reloadRequired) {
      setErrorMessage(null);
    }
  };

  const errorPanel =
    errorMessage === null ? null : (
      <Alert
        type="error"
        showIcon
        message={errorMessage}
        action={
          reloadRequired ? (
            <Button
              loading={refreshing}
              onClick={() => {
                runAsync(reload());
              }}
            >
              Reload Trial
            </Button>
          ) : undefined
        }
      />
    );

  const setupError = productResult.error ?? hydration.error;

  if (trialId.trim() === "") {
    return (
      <Alert
        type="warning"
        showIcon
        message="Save the trial and its plot design before assigning seeds."
      />
    );
  }

  if (trialQuery.isPending && trialQuery.data === undefined) {
    return <div role="status">Loading seed assignments…</div>;
  }

  if (trialQuery.isError || setupError !== null) {
    return (
      <Alert
        type="error"
        showIcon
        message={setupError ?? seedApiError(trialQuery.error).message}
        action={
          <Button
            loading={trialQuery.isFetching}
            onClick={() => {
              runAsync(trialQuery.refetch());
            }}
          >
            Retry
          </Button>
        }
      />
    );
  }

  if (autoAssignmentFailed) {
    return (
      <ErrorAutoScreen
        isRetrying={autoAssigning}
        onRetry={() => {
          runAsync(openAutoAssignment());
        }}
        onClose={() => {
          setAutoAssignmentFailed(false);
        }}
      />
    );
  }

  if (showAutoAssignment) {
    return (
      <div
        className="rd-seeds-tab rd-seeds-tab--assignment"
        data-testid="seeds-tab"
      >
        {errorPanel}

        {autoAssignmentSuccess && (
          <>
            <Alert
              type="success"
              showIcon
              message="Automatic seed assignments are ready to review."
            />
            <div role="note">
              Highlighted map assignments are drafts only; they are not saved
              ownership.
            </div>
          </>
        )}

        <button
          type="button"
          className="rd-seeds-tab__back-heading"
          disabled={busy}
          onClick={closeAssignmentEditor}
        >
          <ArrowLeftOutlined aria-hidden />
          <span>{text("autoProductAssignment")}</span>
        </button>

        <PlotSummary
          totalPlots={tableDraft.length}
          assigned={tableDraft.filter((row) => row.productId !== "").length}
          assignedLabel={text("selected")}
          missingLabel={text("missing")}
          applicationAssigned={
            new Set(
              tableDraft
                .filter((row) => row.productId !== "")
                .map((row) => row.productId)
            ).size
          }
          applicationTotal={products.length}
          applicationLabel={text("products")}
        />

        {tableDraft.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="No plot cells are available for assignment."
          />
        ) : (
          <fieldset
            className="rd-seeds-tab__assignment-fields"
            disabled={busy || reloadRequired}
          >
            <AssignmentGroups
              unit={displayUnit}
              rows={tableDraft}
              productOptions={products.map((product) => ({
                value: product.id,
                label: product.name,
                rateOptions: product.options,
                color: product.color,
                range: product.range,
              }))}
              errors={tableErrors}
              onShowPlot={toggleFocus}
              onAssignmentChange={updateTableAssignment}
            />
          </fieldset>
        )}

        <div className="rd-seeds-tab__footer">
          <Button type="text" disabled={busy} onClick={closeAssignmentEditor}>
            <LeftOutlined aria-hidden />
            {text("back")}
          </Button>

          <Button
            className="rd-seeds-tab__auto-next-btn"
            type="primary"
            loading={saving}
            disabled={!canSaveTable}
            onClick={() => {
              runAsync(saveTable());
            }}
          >
            {text("biologicals")}
            <RightOutlined aria-hidden />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rd-seeds-tab" data-testid="seeds-tab">
      {errorPanel}

      <fieldset
        disabled={busy || reloadRequired}
        style={{ border: 0, padding: 0 }}
      >
        <StdSeedRate
          productOptions={standardOptions}
          productValue={nonEmptyString(standard.productId)}
          onProductChange={(value) => {
            const name =
              standardOptions.find((option) => option.value === value)?.label ??
              "";

            setStandard((previous) => ({
              ...previous,
              productId: value,
              productName: name,
            }));

            setRatesDosage((previous) => ({
              ...previous,
              standard_seed_product_id: value,
              standard_product: name,
              unit_of_rate: unit,
            }));
          }}
          seedRateValue={standard.rate}
          onSeedRateChange={(value) => {
            setStandard((previous) => ({
              ...previous,
              rate: value,
            }));

            setRatesDosage((previous) => ({
              ...previous,
              standard_seed_rate: value ?? 0,
              unit_of_rate: unit,
            }));
          }}
          seedRateLabel={text("seedRateLabel", { unit: displayUnit })}
          seedRateError={
            standard.productId !== "" ? getRateError(standard.rate) : undefined
          }
        />
      </fieldset>

      {catalog.isError && (
        <Alert
          type="warning"
          message="The standard seed catalogue could not be loaded."
          action={
            <Button
              onClick={() => {
                runAsync(catalog.refetch());
              }}
            >
              Retry Catalogue
            </Button>
          }
        />
      )}

      <PlotSummary
        totalPlots={persisted.length}
        assigned={persisted.filter((row) => row.productId !== "").length}
        applicationAssigned={
          new Set(
            persisted
              .filter((row) => row.productId !== "")
              .map((row) => row.productId)
          ).size
        }
        applicationTotal={products.length}
        applicationLabel={text("products")}
      />

      <WarningConfirmationPopover
        open={overwriteConfirmationOpen}
        ariaLabel="Overwrite existing assignments"
        message="Proceeding with auto product assignment will overwrite all current entries."
        onCancel={() => {
          setOverwriteConfirmationOpen(false);
        }}
        onConfirm={() => {
          setOverwriteConfirmationOpen(false);
          runAsync(openAutoAssignment());
        }}
      >
        <AutoProdAssignment
          disabled={
            busy ||
            reloadRequired ||
            !hasFieldStandardVariety ||
            manualProductId !== null ||
            tableDirty ||
            persisted.length === 0
          }
          onClick={() => {
            if (!hasFieldStandardVariety || busy || reloadRequired) return;
            if (overwriteConfirmationOpen) {
              setOverwriteConfirmationOpen(false);
              return;
            }

            if (persisted.some((row) => row.productId !== "")) {
              setOverwriteConfirmationOpen(true);
              return;
            }

            runAsync(openAutoAssignment());
          }}
        />
      </WarningConfirmationPopover>

      {autoAssigning && (
        <div role="status" aria-live="polite">
          Loading automatic seed assignments…
        </div>
      )}

      <div className="rd-seeds-tab__products">
        {products.map((product) => {
          const editing = manualProductId === product.id;
          const owned = persisted.filter(
            (row) => row.productId === product.id && !row.isStandard
          );
          const mapSelected =
            session?.tab === "seeds" &&
            session.ownerId === product.id &&
            session.selectedIds.length > 0;
          const selectedRate = mapSelected
            ? manualPlots.find((plot) => plot.id === session.selectedIds[0])
                ?.seedRate ??
              persisted.find((row) => row.id === session.selectedIds[0])
                ?.seedRate
            : null;

          return (
            <ProductCard
              key={product.id}
              className={
                editing
                  ? "rd-seeds-tab__manual-card"
                  : owned.length > 0
                  ? "rd-seeds-tab__assigned-card"
                  : ""
              }
              productName={product.name}
              indicatorColor={product.color}
              highlightColor={
                mapSelected
                  ? getSeedProductColor(product, products, selectedRate, [
                      selectedRate,
                    ])
                  : owned.length > 0
                  ? product.color
                  : undefined
              }
              rangeText={
                product.range !== undefined
                  ? `${text("range", product.range)} ${displayUnit}`
                  : product.options.length > 0
                  ? `${text("rates", {
                      rates: product.options.join(", "),
                    })} ${displayUnit}`
                  : text("noRange")
              }
              headingAction={
                !editing && owned.length > 0 ? (
                  <span>
                    {text("plotsSelected", {
                      count: owned.length,
                    })}
                  </span>
                ) : undefined
              }
              headerAction={
                editing ? (
                  <ResetBtn
                    disabled={
                      busy ||
                      reloadRequired ||
                      owned.length === 0 ||
                      owned.some((row) => row.legacy)
                    }
                    onClick={() => {
                      runAsync(handleResetSeedAssignments(product.id));
                    }}
                  />
                ) : undefined
              }
              actionLabel={owned.length > 0 ? text("edit") : undefined}
              isSaved={owned.length > 0}
              selectPlotsDisabled={
                busy || reloadRequired || manualProductId !== null
              }
              onSelectPlots={() => {
                openManualProduct(product);
              }}
              footer={
                editing ? (
                  <>
                    <fieldset
                      disabled={busy || reloadRequired}
                      style={{ border: 0, padding: 0 }}
                    >
                      <ManualProductPlots
                        plots={manualPlots}
                        rateOptions={product.options}
                        errors={manualErrors}
                        unit={displayUnit}
                        focusedId={focusedId}
                        onToggleFocus={toggleFocus}
                        onRateChange={(id, value) => {
                          if (busy || reloadRequired) {
                            return;
                          }

                          setManualRates((previous) => ({
                            ...previous,
                            [id]: value,
                          }));
                        }}
                      />
                    </fieldset>

                    {manualPlots.length === 0 && owned.length > 0 && (
                      <Alert
                        type="warning"
                        message={
                          "Use Reset to remove this product's saved " +
                          "assignments, or select plots before saving."
                        }
                      />
                    )}

                    <div className="rd-seeds-tab__manual-actions">
                      <Button disabled={busy} onClick={cancelManual}>
                        {text("cancel")}
                      </Button>

                      <Button
                        className="rd-seeds-tab__manual-save"
                        loading={saving}
                        disabled={!canSaveManual}
                        onClick={() => {
                          runAsync(saveManual());
                        }}
                      >
                        {text("save")}
                      </Button>
                    </div>
                  </>
                ) : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
};

/**
 * Start with fresh local editor state when the trial or workspace changes.
 */
const SeedsTab = (props: SeedsTabProps): JSX.Element => {
  const { trialInfo } = useNewTrialState();
  const { orgId, workspaceId } = useTrialsRouter();

  const editorKey = JSON.stringify([
    orgId ?? "",
    workspaceId ?? "",
    trialInfo?.id ?? "",
  ]);

  return <SeedsTabContent key={editorKey} {...props} />;
};

export default SeedsTab;
